import type { TurnWorld, Unit } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { getMovementCost } from './terrain';

export interface Coord {
  col: number;
  row: number;
}

/**
 * Build the occupied and loadable tile sets for BFS.
 * - occupied: tiles blocked for the moving unit
 * - loadable: tiles with a friendly carrier that has free cargo accepting this unit type
 *   (reachable as destination but BFS doesn't expand from them)
 * - ports: tile keys of all ports (used as a docking exception for water-domain units)
 */
function buildTileSets(
  world: TurnWorld,
  unit: Unit,
  visible?: Set<string>,
): { occupied: Set<string>; friendlyOccupied: Set<string>; loadable: Set<string>; ports: Set<string> } {
  const occupied = new Set<string>();        // enemy units — block completely
  const friendlyOccupied = new Set<string>(); // friendly units — can pass through, can't stop
  const loadable = new Set<string>();
  const ports = new Set<string>();

  for (const [, u] of world.units) {
    if (u.id === unit.id) continue;
    const key = `${u.col},${u.row}`;
    const carrierDef = UNIT_DEFS[u.type];

    // Check if this is a friendly carrier with free cargo that accepts our unit type
    if (
      u.owner === unit.owner &&
      carrierDef.cargoCapacity &&
      (carrierDef.cargoAccepts?.includes(unit.type) || carrierDef.cargoAccepts?.includes(resolveBaseType(unit.type))) &&
      (u.cargo?.length ?? 0) < carrierDef.cargoCapacity
    ) {
      loadable.add(key);
    } else if (u.owner === unit.owner) {
      friendlyOccupied.add(key);
    } else if (!visible || visible.has(key)) {
      // Enemy units block movement. EXCEPTION: an enemy hidden in fog (its tile
      // not in `visible`) does NOT block — otherwise the gap it leaves in the
      // movement range would reveal its position. The ambush mechanic resolves
      // contact during the actual move instead.
      occupied.add(key);
    }
  }

  // Buildings: own training blocks movement; ports are recorded as docking exceptions
  for (const [, b] of world.buildings) {
    if (b.type === 'port') ports.add(`${b.col},${b.row}`);
    if (b.training && b.owner === unit.owner) {
      occupied.add(`${b.col},${b.row}`);
    }
  }

  return { occupied, friendlyOccupied, loadable, ports };
}

/**
 * BFS flood-fill: returns all reachable tiles for a unit within its movement budget.
 * Respects terrain cost, domain passability, and one-unit-per-tile constraint.
 * Friendly carriers with free cargo are reachable but not traversable.
 * Returns a Map of "col,row" → remaining movement budget at that tile.
 */
export function getMovementRange(world: TurnWorld, unit: Unit, zoc?: Set<string>, visible?: Set<string>): Map<string, number> {
  const def = UNIT_DEFS[unit.type];
  const budget = unit.movePoints;
  const domain = def.domain;

  // BFS frontier: [col, row, remainingBudget]
  const visited = new Map<string, number>(); // key → best remaining budget
  const queue: [number, number, number][] = [];

  const startKey = `${unit.col},${unit.row}`;
  visited.set(startKey, budget);
  queue.push([unit.col, unit.row, budget]);

  const { occupied, friendlyOccupied, loadable, ports } = buildTileSets(world, unit, visible);

  const directions = [
    [0, -1], [0, 1], [-1, 0], [1, 0],
  ];

  let head = 0;
  while (head < queue.length) {
    const [col, row, remaining] = queue[head++];

    // Don't expand from loadable tiles (carrier blocks further traversal)
    const currentKey = `${col},${row}`;
    if (loadable.has(currentKey)) continue;

    for (const [dc, dr] of directions) {
      const nc = col + dc;
      const nr = row + dr;

      // Bounds check
      if (nc < 0 || nr < 0 || nc >= world.map.width || nr >= world.map.height) continue;

      const key = `${nc},${nr}`;
      // Can't move through enemy-occupied tiles
      if (occupied.has(key)) continue;

      const cell = world.map.tiles[nr][nc];
      if (cell.impassable) continue; // editor-marked: no unit may enter
      const terrain = cell.type;
      let cost = getMovementCost(terrain, domain, unit.type);
      // Loadable carrier on impassable terrain (e.g. land unit → ship on water): cost 1
      if (cost === Infinity && loadable.has(key)) cost = 1;
      // Ships may dock at a port even though the underlying tile is land.
      if (cost === Infinity && domain === 'water' && ports.has(key)) cost = 1;
      if (cost === Infinity) continue; // impassable

      let newRemaining = remaining - cost;
      if (newRemaining < 0) continue; // not enough budget

      // ZoC: entering an enemy zone-of-control tile consumes all remaining movement
      if (zoc && zoc.has(key) && newRemaining > 0) newRemaining = 0;

      // Only visit if we arrive with more budget than before
      const prev = visited.get(key);
      if (prev !== undefined && prev >= newRemaining) continue;

      visited.set(key, newRemaining);
      queue.push([nc, nr, newRemaining]);
    }
  }

  // Remove the unit's own tile from the result (it's the origin, not a movement target)
  visited.delete(startKey);

  // Remove friendly-occupied tiles — can traverse but not stop on
  for (const key of friendlyOccupied) {
    visited.delete(key);
  }

  return visited;
}

/**
 * Find shortest path (by movement cost) from unit position to target tile.
 * Returns array of coordinates from start (exclusive) to target (inclusive),
 * or null if target is unreachable.
 */
export function findPath(world: TurnWorld, unit: Unit, target: Coord, zoc?: Set<string>, visible?: Set<string>): Coord[] | null {
  const def = UNIT_DEFS[unit.type];
  const budget = unit.movePoints;
  const domain = def.domain;

  const { occupied, friendlyOccupied, loadable, ports } = buildTileSets(world, unit, visible);

  const targetKey = `${target.col},${target.row}`;

  // Can't path to a blocked tile (enemy or friendly-occupied, unless it's loadable)
  if (occupied.has(targetKey)) return null;
  if (friendlyOccupied.has(targetKey)) return null;

  // BFS with cost tracking + parent pointers
  const costSoFar = new Map<string, number>(); // key → cost spent to reach
  const parent = new Map<string, string | null>(); // key → parent key
  const queue: [number, number, number][] = []; // col, row, costSpent

  const startKey = `${unit.col},${unit.row}`;
  costSoFar.set(startKey, 0);
  parent.set(startKey, null);
  queue.push([unit.col, unit.row, 0]);

  const directions = [
    [0, -1], [0, 1], [-1, 0], [1, 0],
  ];

  let head = 0;
  while (head < queue.length) {
    const [col, row, spent] = queue[head++];
    const currentKey = `${col},${row}`;

    if (currentKey === targetKey) {
      // Reconstruct path
      const path: Coord[] = [];
      let key: string | null | undefined = targetKey;
      while (key && key !== startKey) {
        const [c, r] = key.split(',').map(Number);
        path.unshift({ col: c, row: r });
        key = parent.get(key);
      }
      return path;
    }

    // Don't expand from loadable tiles (carrier blocks further traversal)
    if (loadable.has(currentKey)) continue;

    for (const [dc, dr] of directions) {
      const nc = col + dc;
      const nr = row + dr;

      if (nc < 0 || nr < 0 || nc >= world.map.width || nr >= world.map.height) continue;

      const key = `${nc},${nr}`;
      // Enemy units block completely
      if (occupied.has(key)) continue;

      const cell = world.map.tiles[nr][nc];
      if (cell.impassable) continue; // editor-marked: no unit may enter
      const terrain = cell.type;
      let cost = getMovementCost(terrain, domain, unit.type);
      // Loadable carrier on impassable terrain (e.g. land unit → ship on water): cost 1
      if (cost === Infinity && loadable.has(key)) cost = 1;
      // Ships may dock at a port even though the underlying tile is land.
      if (cost === Infinity && domain === 'water' && ports.has(key)) cost = 1;
      if (cost === Infinity) continue;

      let newSpent = spent + cost;
      if (newSpent > budget) continue;

      // ZoC: entering an enemy zone-of-control tile consumes all remaining movement
      if (zoc && zoc.has(key) && newSpent < budget) newSpent = budget;

      const prevCost = costSoFar.get(key);
      if (prevCost !== undefined && prevCost <= newSpent) continue;

      costSoFar.set(key, newSpent);
      parent.set(key, currentKey);
      queue.push([nc, nr, newSpent]);
    }
  }

  return null; // target not reachable
}

/** Parse a "col,row" key back to Coord. */
export function parseCoordKey(key: string): Coord {
  const [col, row] = key.split(',').map(Number);
  return { col, row };
}
